import { useEffect, useState } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, CalendarDays, Phone } from "lucide-react";
import { API, auth, handleAuthError } from "./shared";

const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
const HOURS = Array.from({ length: 10 }, (_, i) => `${String(8 + i).padStart(2, "0")}:00`);
const iso = (d) => d.toISOString().slice(0, 10);

const mondayOf = (d) => {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - day);
  x.setHours(12, 0, 0, 0);
  return x;
};

const AdminCalendar = ({ token, logout }) => {
  const [quotes, setQuotes] = useState([]);
  const [closed, setClosed] = useState([]);
  const [monday, setMonday] = useState(() => mondayOf(new Date()));

  useEffect(() => {
    axios.get(`${API}/quotes`, auth(token)).then((r) => setQuotes(r.data.filter((q) => q.date && q.time))).catch((err) => handleAuthError(err, logout, () => {}));
    axios.get(`${API}/closed-days`).then((r) => setClosed(r.data.map((d) => d.date))).catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const days = DAYS.map((label, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return { label, date: iso(d), num: d.getDate() };
  });
  const today = iso(new Date());
  const shift = (n) => setMonday((m) => { const x = new Date(m); x.setDate(x.getDate() + n * 7); return x; });
  const at = (date, time) => quotes.filter((q) => q.date === date && q.time === time);
  const weekCount = quotes.filter((q) => days.some((d) => d.date === q.date)).length;
  const range = `${days[0].num} ${monday.toLocaleDateString("fr-FR", { month: "short" })} → ${days[5].num} ${new Date(monday.getTime() + 5 * 864e5).toLocaleDateString("fr-FR", { month: "short", year: "numeric" })}`;

  return (
    <div data-testid="admin-calendar-tab">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display flex items-center gap-2 text-lg font-bold text-forest"><CalendarDays className="h-5 w-5 text-gold" /> Rendez-vous de la semaine</h2>
          <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-brand" data-testid="calendar-range">{range} · {weekCount} rendez-vous</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} data-testid="calendar-prev" className="flex h-10 w-10 items-center justify-center rounded-full border border-brand/20 text-brand hover:bg-mint" aria-label="Semaine précédente"><ChevronLeft className="h-4 w-4" /></button>
          <button onClick={() => setMonday(mondayOf(new Date()))} data-testid="calendar-today" className="rounded-full border border-brand/20 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-brand hover:bg-mint">Aujourd'hui</button>
          <button onClick={() => shift(1)} data-testid="calendar-next" className="flex h-10 w-10 items-center justify-center rounded-full border border-brand/20 text-brand hover:bg-mint" aria-label="Semaine suivante"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="mt-6 overflow-x-auto rounded-3xl border border-brand/10 bg-white">
        <div className="grid min-w-[760px] grid-cols-[64px_repeat(6,1fr)]">
          <div className="border-b border-brand/10 bg-mint" />
          {days.map((d) => (
            <div key={d.date} data-testid={`calendar-day-${d.date}`} className={`border-b border-l border-brand/10 px-3 py-3 text-center ${closed.includes(d.date) ? "bg-cream" : "bg-mint"}`}>
              <p className="font-mono text-[10px] uppercase tracking-widest text-brand">{d.label}</p>
              <p className={`font-display mx-auto mt-1 flex h-8 w-8 items-center justify-center rounded-full text-base font-bold ${d.date === today ? "bg-gold text-white" : "text-forest"}`}>{d.num}</p>
              {closed.includes(d.date) && <p className="mt-1 font-mono text-[9px] uppercase tracking-widest text-gold">Fermé</p>}
            </div>
          ))}
          {HOURS.map((h) => (
            <div key={h} className="contents">
              <div className="border-b border-brand/10 px-2 py-3 text-right font-mono text-[11px] text-brand">{h}</div>
              {days.map((d) => (
                <div key={d.date + h} className={`min-h-[56px] border-b border-l border-brand/10 p-1.5 ${closed.includes(d.date) ? "bg-cream/40" : ""}`}>
                  {at(d.date, h).map((q) => (
                    <div key={q.id} data-testid={`calendar-event-${q.id}`} className={`rounded-xl px-2.5 py-2 text-xs text-white shadow-sm ${q.status === "new" ? "bg-gold" : "bg-brand"}`}>
                      <p className="font-display truncate font-bold">{q.name}</p>
                      <p className="truncate opacity-90">{q.service}{q.vehicle ? ` · ${q.vehicle}` : ""}</p>
                      <a href={`tel:${q.phone.replace(/\s/g, "")}`} className="mt-1 inline-flex items-center gap-1 font-mono text-[10px] opacity-90 hover:underline"><Phone className="h-3 w-3" />{q.phone}</a>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      <p className="mt-4 flex flex-wrap gap-4 font-mono text-[10px] uppercase tracking-widest text-brand">
        <span className="flex items-center gap-2"><span className="h-3 w-3 rounded bg-gold" /> Nouvelle demande</span>
        <span className="flex items-center gap-2"><span className="h-3 w-3 rounded bg-brand" /> Traitée</span>
        <span className="flex items-center gap-2"><span className="h-3 w-3 rounded border border-gold/40 bg-cream" /> Jour fermé</span>
      </p>
    </div>
  );
};

export default AdminCalendar;
